import { useState } from "react";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";
import { Copy, Link2, Loader2 } from "lucide-react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { createMagicLink } from "@/lib/me-api";

export function MagicLinkPanel() {
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate() {
    setError(null);
    setIsGenerating(true);
    try {
      const result = await createMagicLink();
      setUrl(result.url);
      setExpiresAt(result.expires_at);
      const dataUrl = await QRCode.toDataURL(result.url, {
        width: 200,
        margin: 2,
        errorCorrectionLevel: "M",
      });
      setQrDataUrl(dataUrl);
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "生成失败，请稍后重试");
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleCopy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("链接已复制");
    } catch {
      toast.error("复制失败，请手动选中链接复制");
    }
  }

  const expiresLabel =
    expiresAt &&
    format(new Date(expiresAt), "yyyy年M月d日 HH:mm", {
      locale: zhCN,
    });

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Link2 className="size-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-1">
            <h2 className="text-base font-medium">登录链接</h2>
            <p className="text-sm text-muted-foreground">
              生成后 10 分钟内有效，只能使用一次；复制或扫码在其他设备打开即可登录。
            </p>
          </div>

          <Button type="button" disabled={isGenerating} onClick={() => void handleGenerate()}>
            {isGenerating ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                生成中…
              </>
            ) : url ? (
              "重新生成登录链接"
            ) : (
              "生成登录链接"
            )}
          </Button>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          {url ? (
            <div className="space-y-3 rounded-md border border-dashed bg-muted/20 p-3">
              <div className="flex gap-2">
                <Input readOnly value={url} aria-label="登录链接" className="font-mono text-xs" />
                <Button type="button" variant="outline" size="icon" onClick={() => void handleCopy()} aria-label="复制链接">
                  <Copy className="size-4" />
                </Button>
              </div>
              {expiresLabel ? (
                <p className="text-xs text-muted-foreground">有效期至 {expiresLabel}</p>
              ) : null}
              {qrDataUrl ? (
                <div className="flex justify-center pt-1">
                  <img src={qrDataUrl} alt="登录链接二维码" className="size-[200px] rounded-md border bg-white p-2" />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
